import React, { useEffect, useState } from "react";
import { Button } from "@component/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { RadioGroup, RadioGroupItem } from "@component/radio-group";
import { Popover, PopoverContent, PopoverTrigger } from "@component/popover";
import { CheckIcon, ChevronDownIcon, MailIcon, UserCheckIcon, XIcon } from "lucide-react";
import { addReviewer } from "@service/reviewer";
import { ReviewerSessionDays } from "@type/reviewer";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { isValidEmail } from "@util/string";

interface ReviewerInviteItem {
    id: string;
    email: string;
    session_duration_days: ReviewerSessionDays;
}

interface AddReviewerDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    workspaceId: string;
    onSuccess: () => void;
}

const SESSION_DURATION_OPTIONS: { value: ReviewerSessionDays; label: string; description: string }[] = [
    { value: 14, label: "14 days", description: "Re-verify every 2 weeks" },
    { value: 30, label: "30 days", description: "Re-verify every month" },
    { value: 90, label: "90 days", description: "Re-verify every 3 months" },
];

export default function AddReviewerDialog({
    open,
    onOpenChange,
    workspaceId,
    onSuccess,
}: AddReviewerDialogProps) {
    const snackbar = useSnackbar();
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [reviewerList, setReviewerList] = useState<ReviewerInviteItem[]>([]);
    const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) {
            setEmail("");
            setEmailError("");
            setShowDropdown(false);
            setReviewerList([]);
            setOpenPopoverId(null);
            setIsLoading(false);
        }
    }, [open]);

    useEffect(() => {
        setShowDropdown(isValidEmail(email.trim()));
    }, [email]);

    const handleClose = () => {
        onOpenChange(false);
    };

    const addEmailToList = () => {
        const trimmed = email.trim();
        if (!isValidEmail(trimmed)) {
            setEmailError("Please enter a valid email address.");
            return;
        }
        if (reviewerList.some((r) => r.email.toLowerCase() === trimmed.toLowerCase())) {
            setEmailError("This email has already been added.");
            return;
        }
        setReviewerList([...reviewerList, {
            id: `reviewer-${Date.now()}`,
            email: trimmed,
            session_duration_days: 30,
        }]);
        setEmail("");
        setEmailError("");
        setShowDropdown(false);
        inputRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            addEmailToList();
        }
    };

    const handleDurationChange = (id: string, duration: ReviewerSessionDays) => {
        setReviewerList(reviewerList.map((r) => r.id === id ? { ...r, session_duration_days: duration } : r));
        setOpenPopoverId(null);
    };

    const handleRemove = (id: string) => {
        setReviewerList(reviewerList.filter((r) => r.id !== id));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (reviewerList.length === 0) return;

        setIsLoading(true);

        const results = await Promise.allSettled(
            reviewerList.map((item) =>
                addReviewer(workspaceId, {
                    email: item.email,
                    session_duration_days: item.session_duration_days,
                })
            )
        );

        const succeeded = results.filter((r) => r.status === "fulfilled").length;
        const failed = results.filter((r) => r.status === "rejected").length;

        if (succeeded > 0) {
            snackbar.showSuccess(
                `${succeeded} invitation${succeeded !== 1 ? "s" : ""} sent successfully!`
            );
            onSuccess();
        }
        if (failed > 0) {
            snackbar.showError(
                `${failed} invitation${failed !== 1 ? "s" : ""} failed to send. Please try again.`
            );
        }

        setIsLoading(false);

        if (failed === 0) {
            handleClose();
        } else {
            const failedEmails = results
                .map((r, i) => r.status === "rejected" ? reviewerList[i] : null)
                .filter(Boolean) as ReviewerInviteItem[];
            setReviewerList(failedEmails);
        }

        catchErrorLog(`addReviewer batch: ${succeeded} succeeded, ${failed} failed`);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl" closeButtonClassName="right-4 top-4">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <UserCheckIcon className="h-5 w-5 text-blue-600" />
                        Add Reviewer
                    </DialogTitle>
                    <DialogDescription>
                        Reviewers can only view candidate profiles shared directly with them.
                        Each reviewer will receive an activation email to verify their identity.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 space-y-2">
                        <p className="text-xs font-medium text-gray-700">Reviewers will have access to:</p>
                        <ul className="space-y-1.5">
                            {[
                                "Candidate profile and CV",
                                "Match score breakdown",
                                "AI interview insights",
                                "Form and eligibility check responses (if enabled)",
                            ].map((item) => (
                                <li key={item} className="flex items-center gap-2 text-xs text-gray-600">
                                    <CheckIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="reviewer-email">Invite by email</Label>
                        <div className="relative">
                            <Input
                                ref={inputRef}
                                id="reviewer-email"
                                type="email"
                                placeholder="Enter an email address"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (emailError) setEmailError("");
                                }}
                                onKeyDown={handleKeyDown}
                                className={`w-full pl-10 ${emailError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                disabled={isLoading}
                                autoFocus
                            />
                            <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />

                            {showDropdown && email.trim() && !emailError && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                                    <button
                                        type="button"
                                        onClick={addEmailToList}
                                        className="w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 group"
                                    >
                                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                            <span className="text-sm font-medium text-blue-600">
                                                {email.trim().charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {email.trim()}
                                            </p>
                                        </div>
                                        <div className="text-xs text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                            Add
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>

                        {emailError ? (
                            <p className="text-xs text-red-600">{emailError}</p>
                        ) : (
                            <p className="text-xs text-gray-500">
                                Add multiple email addresses and set their session duration below.
                            </p>
                        )}
                    </div>

                    {reviewerList.length > 0 && (
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Workspace reviewers</Label>
                            <div className="rounded-lg border border-gray-200 max-h-64 overflow-y-auto divide-y divide-gray-100">
                                {reviewerList.map((reviewer) => (
                                    <div
                                        key={reviewer.id}
                                        className="flex items-center justify-between p-3"
                                    >
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                <span className="text-sm font-medium text-blue-600">
                                                    {reviewer.email.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <p className="text-sm font-medium truncate">
                                                {reviewer.email}
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <Popover
                                                open={openPopoverId === reviewer.id}
                                                onOpenChange={(open) =>
                                                    setOpenPopoverId(open ? reviewer.id : null)
                                                }
                                            >
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        className={`h-9 px-3 justify-between font-normal hover:bg-blue-50 ${openPopoverId === reviewer.id ? "bg-blue-50" : ""}`}
                                                        disabled={isLoading}
                                                    >
                                                        <span className="text-sm">
                                                            {SESSION_DURATION_OPTIONS.find(
                                                                (o) => o.value === reviewer.session_duration_days
                                                            )?.label ?? "30 days"}
                                                        </span>
                                                        <ChevronDownIcon className="h-4 w-4 ml-2 text-gray-400" />
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-72 p-0" align="end">
                                                    <div className="p-4">
                                                        <h4 className="font-semibold text-sm mb-1">Session Duration</h4>
                                                        <p className="text-xs text-gray-500 mb-3">
                                                            How long before they need to re-verify on a new device.
                                                        </p>
                                                        <RadioGroup
                                                            value={String(reviewer.session_duration_days)}
                                                            onValueChange={(val) =>
                                                                handleDurationChange(
                                                                    reviewer.id,
                                                                    Number(val) as ReviewerSessionDays
                                                                )
                                                            }
                                                            className="space-y-2"
                                                        >
                                                            {SESSION_DURATION_OPTIONS.map((option) => (
                                                                <label
                                                                    key={option.value}
                                                                    className={`flex items-start gap-3 cursor-pointer p-2 rounded-md hover:bg-blue-50 transition-colors ${
                                                                        reviewer.session_duration_days === option.value
                                                                            ? "bg-blue-50"
                                                                            : ""
                                                                    }`}
                                                                >
                                                                    <RadioGroupItem
                                                                        value={String(option.value)}
                                                                        className="mt-0.5"
                                                                    />
                                                                    <div className="flex-1">
                                                                        <div className="font-medium text-sm">
                                                                            {option.label}
                                                                        </div>
                                                                        <div className="text-xs text-muted-foreground">
                                                                            {option.description}
                                                                        </div>
                                                                    </div>
                                                                </label>
                                                            ))}
                                                        </RadioGroup>
                                                    </div>
                                                </PopoverContent>
                                            </Popover>

                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-9 w-9 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                                onClick={() => handleRemove(reviewer.id)}
                                                disabled={isLoading}
                                            >
                                                <XIcon className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <DialogFooter className="gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="bg-blue-600 hover:bg-blue-700"
                            disabled={isLoading || reviewerList.length === 0}
                        >
                            {isLoading
                                ? "Sending..."
                                : `Send ${reviewerList.length > 0 ? `${reviewerList.length} ` : ""}Invitation${reviewerList.length !== 1 ? "s" : ""}`}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
